import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCurrentStateAndParams } from "@uirouter/react";
import { noop, uniq } from "lodash";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { IconButton, ListContentLoader, BasicModal } from "@similarweb/ui-components";

import { showSuccessToast } from "app/actions/toast_actions";
import * as utils from "app/components/filters-bar/utils";
import { ListFooter } from "app/components/React/list-editor/ListFooter";
import { ListTitle } from "app/components/React/list-editor/ListTitle";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { BasicDurations } from "app/services/DurationService";
import type { IUserKeywordGroup } from "app/services/keyword-groups";
import {
    KEYWORD_GROUP_PREFIX,
    useCreateOrUpdateKeywordGroup,
    useDeleteKeywordGroup,
} from "app/services/keyword-groups";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { KeywordsGroupUtilities } from "app/UtilitiesAndConstants/UtilityFunctions/KeywordsGroupUtilities";
import type { IProModalCustomStyles } from "pro-features/components/Modals/src/ProModal";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { KeywordGroupEditorRef } from "./KeywordGroupEditor";
import { KeywordGroupEditor } from "./KeywordGroupEditor";
import { emptyKeywordGroup, KeywordGroupEditorHelpers } from "./KeywordGroupEditorHelpers";
import type { NewGroupTracker } from "./KeywordsGroupsTracking";
import { KeywordsGroupsTracking } from "./KeywordsGroupsTracking";
import { KeywordsSuggestions } from "./KeywordsSuggestions";
import type { IKeywordsGroupEditorController } from "./useKeywordsGroupEditorController";
import { getGroupName } from "./utils/getGroupName";
import { allTrackers } from "app/services/track/track";

const ERROR_TIMEOUT = 5_000;
const LIST_ROW_HEIGHT = 44;

const KeywordEditorContainer = styled.div<{ error: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    border-radius: 4px;
    margin-bottom: 48px;
    border: 1px solid ${(props) => (props.error ? colorsPalettes.red[400] : colorsPalettes.carbon[100])};

    height: 520px;
    @media (max-height: 768px) {
        height: 420px;
    }
`;

const FullHeightLoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${colorsPalettes.carbon[0]};
    z-index: 10;
`;

const KeywordGroupHeader = styled.div`
    display: flex;
    justify-content: space-between;
    margin: 16px 0 12px;
    > span {
        ${setFontVariant("body2", colorsPalettes.carbon[300])}
    }
`;

const LoadSuggestionsBanner = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 56px;
    padding: 16px;
    box-sizing: border-box;
    background-color: ${colorsPalettes.sky[100]};
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;

const SuggestionsBannerText = styled(I18n)`
    ${setFontVariant("body2", colorsPalettes.carbon[500])};
`;

interface IKeywordsGroupEditorModalProps {
    onClose: () => void;
    open?: boolean;
    keywordsGroup?: IUserKeywordGroup;
    seedKeyword?: string;
    onSave?: (modifiedGroup: IUserKeywordGroup) => void;
    onDelete?: (deletedGroup: IUserKeywordGroup) => void;
    onBeforeDelete?: (group: IUserKeywordGroup) => void;
    showDeleteButton?: boolean;
    onEditorOpened?: (editor: HTMLElement) => void;
    onEditorClosed?: (editor: HTMLElement) => void;
    goToViewGroup?(keywordGroup: IUserKeywordGroup): void;
    editTargetState?: string;
    showGoToMonitorKeywordListButton?: boolean;
    onEdit?: (modifiedGroup: IUserKeywordGroup) => void;
    maxKeywords?: number;
    ignoreDuplicateCheck?: boolean;
    isEditToastWithLink?: boolean;
    onSuccessToastLinkClick?: (id: string) => void;
    controller?: IKeywordsGroupEditorController;
}

const isNewGroup = (group: IUserKeywordGroup) => {
    return !group.Id;
};

const getTracker = (group?: IUserKeywordGroup) => {
    const { existingGroupTracker, newGroupTracker, genericGroupTracker } = KeywordsGroupsTracking;
    if (!group) {
        return genericGroupTracker;
    } else if (isNewGroup(group)) {
        return newGroupTracker;
    } else {
        return existingGroupTracker;
    }
};

export const KeywordsGroupEditorModal = ({
    open = false,
    keywordsGroup,
    seedKeyword,
    showDeleteButton,
    onSave,
    onClose,
    onDelete,
    onBeforeDelete,
    onEditorOpened = noop,
    onEditorClosed = noop,
    goToViewGroup,
    editTargetState,
    showGoToMonitorKeywordListButton: _showGoToMonitorKeywordListButton = false,
    onEdit = noop,
    maxKeywords,
    ignoreDuplicateCheck = false,
    isEditToastWithLink = true,
    onSuccessToastLinkClick,
    controller,
}: IKeywordsGroupEditorModalProps) => {
    const { params } = useCurrentStateAndParams();
    const [isOpen, setIsOpen] = useState(false);
    const [saveSuccessful, setSaveSuccessful] = useState(false);
    const [group, setGroup] = useState<IUserKeywordGroup>(
        keywordsGroup ?? controller?.session.keywordsGroup ?? emptyKeywordGroup,
    );
    const [error, setError] = useState<string | undefined>();
    const [titleError, setTitleError] = useState<string | undefined>();
    const [isSaving, setIsSaving] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [shouldAutoAddSuggestions, setShouldAutoAddSuggestions] = useState(true);
    const [shouldShowSuggestions, setShouldShowSuggestions] = useState(
        Boolean(
            (keywordsGroup ?? controller?.session.keywordsGroup ?? emptyKeywordGroup).Keywords?.length ||
            seedKeyword != null,
        ),
    );
    const initialKeywordsGroupRef = useRef<IUserKeywordGroup>(
        keywordsGroup ?? controller?.session.keywordsGroup ?? emptyKeywordGroup,
    );
    const listContainerRef = useRef<KeywordGroupEditorRef | null>(null);
    const previousControllerSessionKeyRef = useRef<number | undefined>(controller?.sessionKey);
    const i18n = i18nFilter();
    const dispatch = useDispatch();
    const maxItems: number = maxKeywords ?? (KeywordGroupEditorHelpers.getMaxGroupCount() as number);
    const { mutateAsync: deleteKeywordGroup } = useDeleteKeywordGroup();
    const createOrUpdateKeywordGroup = useCreateOrUpdateKeywordGroup();

    const tracker = useMemo(() => {
        return getTracker(group);
    }, [group]);

    const proModalStyles: IProModalCustomStyles = useMemo(
        () => ({
            overlay: { zIndex: 10000 },
        }),
        [],
    );

    const initializeModalSession = useCallback(
        async (nextInitialKeywordsGroup: IUserKeywordGroup = emptyKeywordGroup, nextSeedKeyword?: string) => {
            initialKeywordsGroupRef.current = nextInitialKeywordsGroup;
            setSaveSuccessful(false);
            setGroup(nextInitialKeywordsGroup);
            setIsInitialLoad(true);
            setError(undefined);
            setShouldShowSuggestions(!!nextInitialKeywordsGroup.Keywords?.length || nextSeedKeyword != null);
            setIsOpen(true);

            onEditorOpened(listContainerRef.current as never);
            setTimeout(() => listContainerRef.current?.focusInput?.());

            if (nextSeedKeyword && !nextInitialKeywordsGroup.Keywords.length) {
                const groupName = await getGroupName(nextSeedKeyword);
                const newGroup = {
                    ...emptyKeywordGroup,
                    Keywords: [nextSeedKeyword],
                    Name: groupName,
                };
                setGroup(newGroup);
            }
        },
        [onEditorOpened],
    );

    useEffect(() => {
        if (controller) {
            return;
        }

        const nextInitialKeywordsGroup = keywordsGroup ?? emptyKeywordGroup;

        void (async () => {
            const shouldModalOpen = open && !isOpen;
            if (shouldModalOpen) {
                await initializeModalSession(nextInitialKeywordsGroup, seedKeyword);
                return;
            }

            setIsOpen(open);
        })();
    }, [controller, keywordsGroup, open, isOpen, seedKeyword, initializeModalSession]);

    useEffect(() => {
        if (!controller) {
            return;
        }

        const nextSessionKey = controller.sessionKey;
        const shouldInitializeSession = previousControllerSessionKeyRef.current !== nextSessionKey && controller.isOpen;

        previousControllerSessionKeyRef.current = nextSessionKey;

        if (shouldInitializeSession) {
            void initializeModalSession(
                controller.session.keywordsGroup ?? emptyKeywordGroup,
                controller.session.seedKeyword,
            );
            return;
        }

        setIsOpen(controller.isOpen);
    }, [controller, initializeModalSession]);

    const handleClose = (nextSaveSuccessful = saveSuccessful) => {
        setIsOpen(false);
        controller?.hide();
        onEditorClosed(nextSaveSuccessful as never);
        onClose();
    };

    const handleAfterListChange = (keywordsAddedOrRemoved: number) => {
        setError(undefined);
        if (!isInitialLoad) {
            requestAnimationFrame(() => {
                const pixelsMoved = keywordsAddedOrRemoved * LIST_ROW_HEIGHT;
                const listElement = listContainerRef.current?.getListElement();
                const selectedKeywordsListElHeight = (listElement?.firstChild as HTMLElement).getBoundingClientRect()
                    .height;

                if (
                    typeof listElement?.scrollTop === "number" &&
                    listElement.scrollTop <= selectedKeywordsListElHeight
                ) {
                    listElement.scrollTop += pixelsMoved;
                }
            });
        }
    };

    const handleListError = (newError: string) => {
        setError(i18n(newError));
        setTimeout(() => {
            setError(undefined);
        }, ERROR_TIMEOUT);
    };

    const onAddSuggested = (suggestedKeyword: string) => {
        const newKeywords = [...group.Keywords, suggestedKeyword];
        if (newKeywords.length > maxItems) {
            handleListError("KeywordAnalysis.keywordgroup.wizard.error.list.is.full");
            return;
        }
        setGroup((prevState) => ({ ...prevState, Keywords: newKeywords }));
        handleAfterListChange(1);
    };

    const onAddMultipleSuggested = (suggestedKeywords: string[]) => {
        let newKeywords = uniq([...group.Keywords, ...suggestedKeywords]);
        let shouldDisplayError = false;
        if (newKeywords.length > maxItems) {
            shouldDisplayError = true;
            newKeywords = newKeywords.slice(0, maxItems);
        }
        setGroup((prevState) => ({ ...prevState, Keywords: newKeywords }));
        handleAfterListChange(newKeywords.length - group.Keywords.length);

        if (shouldDisplayError) {
            handleListError("KeywordAnalysis.keywordgroup.wizard.error.list.is.full");
        }
    };

    const handleKeywordListChange = (keywords: string[]) => {
        const lowerCasedKeywords = keywords.map((keyword) => keyword.toLowerCase());
        if (!lowerCasedKeywords.length) {
            setShouldShowSuggestions(false);
        } else if (new Set(lowerCasedKeywords).size !== lowerCasedKeywords.length) {
            handleListError("keywordanalysis.groupwizard.duplicatekeywords");
            return;
        }
        setGroup((prevGroup) => ({
            ...prevGroup,
            Keywords: lowerCasedKeywords,
        }));
        handleAfterListChange(keywords.length - group.Keywords?.length);
    };

    const handleDelete = async () => {
        if (onBeforeDelete) {
            onBeforeDelete(group);
            return;
        }

        await deleteKeywordGroup({ group });
        allTrackers.dispatchTrackEvent("Pop Up", "click", "Create New Keyword Group/Delete");
        setSaveSuccessful(true);
        handleClose(true);
        dispatch(showSuccessToast(translate("delete.list.wizard.toast", { listName: group.Name })));
        onDelete?.(group);
    };

    const handleSave = async () => {
        if (isSaving) {
            return;
        }
        setIsSaving(true);
        const existingKeywordGroup = await KeywordsGroupUtilities.getUserGroupByName(group.Name);
        const groupId = group.Id?.startsWith("*") ? group.Id?.substring(1) : group.Id;
        if (
            !ignoreDuplicateCheck &&
            existingKeywordGroup?.Id != null &&
            existingKeywordGroup.Id !== groupId &&
            (existingKeywordGroup.Id?.length || isNewGroup(group))
        ) {
            setTitleError(i18n("keywordanalysis.groupwizard.duplicatename"));
            setIsSaving(false);
            return;
        }
        let modifiedGroup: IUserKeywordGroup | undefined;

        const goToTargetState = (formattedGroupId, reload = true) => {
            let targetState;
            if (editTargetState) {
                targetState = editTargetState;
            } else if (params.keyword?.length) {
                targetState = proNavigator.current();
            } else {
                targetState = "organicsearch_keywordAnalysis_overview_2";
            }
            const topCountries = utils.getCountries();
            void proNavigator.go(
                targetState,
                {
                    ...proNavigator.current().params,
                    keyword: formattedGroupId,
                    ...(!proNavigator.current().params?.country && targetState !== "digitalsuite_monitorkeywords"
                        ? { country: topCountries[0].id, duration: "3m" }
                        : {}),
                },
                { reload },
            );
        };

        const trimmedTitle = group.Name.trim();
        try {
            if (group.Name !== trimmedTitle) {
                setGroup((state) => ({ ...state, Name: trimmedTitle }));
            }
            modifiedGroup = await createOrUpdateKeywordGroup.mutateAsync({ ...group, Id: groupId, Name: trimmedTitle });
        } catch {
            setIsSaving(false);
            return;
        }
        const isEditedGroup = !!initialKeywordsGroupRef.current.Id;
        if (isEditedGroup && modifiedGroup) {
            let toast = getToastItemComponent({
                text: i18n("KeywordAnalysis.keywordgroup.wizard.toast.modified", {
                    keyword: `"${modifiedGroup.Name}"`,
                }),
            });

            if (isEditToastWithLink) {
                toast = getToastItemComponent({
                    text: i18n("KeywordAnalysis.keywordgroup.wizard.toast.modified", {
                        keyword: `"${modifiedGroup.Name}"`,
                    }),
                    linkText: i18n("KeywordAnalysis.keywordgroup.wizard.toast.created.cta"),
                    onClick: () => {
                        if (onSuccessToastLinkClick) {
                            onSuccessToastLinkClick(modifiedGroup.Id);
                        } else {
                            goToTargetState(KEYWORD_GROUP_PREFIX + modifiedGroup.Id);
                        }
                    },
                });
            }

            dispatch(showSuccessToast(toast));
            onEdit?.(modifiedGroup);
        } else if (modifiedGroup && onSave) {
            onSave(modifiedGroup);
        }
        setSaveSuccessful(true);
        setIsSaving(false);
        handleClose(true);
        allTrackers.dispatchTrackEvent("Pop Up", "click", "Create New Keyword Group/Save");
        if (typeof goToViewGroup === "function" && modifiedGroup) {
            goToViewGroup(modifiedGroup);
            return;
        }
    };

    const handleLoadSuggestions = () => {
        setShouldShowSuggestions(true);
        setShouldAutoAddSuggestions(false);
        allTrackers.dispatchTrackEvent("Button", "click", "Create New Keyword Group/Load Suggestions");
    };

    const handleGroupTitleChange = (title: string) => {
        setTitleError(undefined);
        setGroup((state) => ({ ...state, Name: title }));
    };

    const handleGoToMonitorKeywordList = () => {
        allTrackers.dispatchTrackEvent("Button", "click", `view list in my keyword lists/${group.Name || ""}`);
        void proNavigator.go("digitalsuite_monitorkeywords", {
            keywordGroupId: group.Id,
            country: params.country || utils.getCountries()[0].id,
            duration: BasicDurations.LAST_TWELVE_MONTHS,
            webSource: devicesTypes.DESKTOP,
            keywordsType: "both",
            keyword: `*${group.Id}`,
        });
    };

    const shouldShowFullHeightLoader = shouldShowSuggestions && isInitialLoad && isNewGroup(group);

    const saveButtonTooltip = () => {
        if (!group.Name?.length) {
            return i18n("KeywordAnalysis.keywordgroup.wizard.save.button.tooltip.missing.name");
        }
        if (!group.Keywords?.length) {
            return i18n("KeywordAnalysis.keywordgroup.wizard.save.button.tooltip.missing.keywords");
        }
    };

    return (
        <BasicModal
            className="keywordsGroupEditorModal"
            customStyles={proModalStyles}
            isOpen={isOpen}
            shouldCloseOnOverlayClick={false}
            closeIconConfig={{ show: false }}
            onRequestClose={() => handleClose()}
        >
            <div>
                <ListTitle
                    titleText={i18n(
                        isNewGroup(group)
                            ? "KeywordAnalysis.keywordgroup.wizard.create"
                            : "KeywordAnalysis.keywordgroup.wizard.edit",
                    )}
                    placeHolderText={i18n("KeywordAnalysis.keywordgroup.wizard.placeholder")}
                    title={{
                        isValid: titleError === undefined,
                        text: group.Name,
                        errorMessage: titleError,
                    }}
                    // TODO: remove once we have a proper solution for onSave callback
                    showGoToMonitorKeywordListButton={false}
                    onGoToMonitorKeywordList={handleGoToMonitorKeywordList}
                    onListTitleFocus={() => setTitleError(undefined)}
                    onListTitleChange={handleGroupTitleChange}
                    tracker={tracker}
                    preventFocusOnMount
                />
                <KeywordGroupHeader>
                    <I18n>KeywordAnalysis.keywordgroup.wizard.header.title</I18n>
                    {error ? (
                        <i
                            data-scss-tooltip={i18n(error)}
                            // eslint-disable-next-line max-len
                            className="customCategoriesWizard-invalid-icon customCategoriesWizard-editor-invalid-format iconInfo iconInfo--white u-alignRight scss-tooltip scss-tooltip--w"
                        >
                            !
                        </i>
                    ) : (
                        <I18n
                            dataObj={{
                                selected: group.Keywords?.length ?? 0,
                                max:
                                    maxItems > 10_000
                                        ? i18n("KeywordAnalysis.keywordgroup.wizard.header.counter.unlimited")
                                        : maxItems,
                            }}
                        >
                            KeywordAnalysis.keywordgroup.wizard.header.counter
                        </I18n>
                    )}
                </KeywordGroupHeader>
                <KeywordEditorContainer error={!!error}>
                    {shouldShowFullHeightLoader ? (
                        <FullHeightLoadingContainer data-automation="keyword-group-editor-full-height-loader">
                            <ListContentLoader
                                loaderTitle={i18n("KeywordAnalysis.keywordgroup.wizard.loader.title")}
                                loaderSubtitle={i18n("KeywordAnalysis.keywordgroup.wizard.loader.subtitle")}
                            />
                        </FullHeightLoadingContainer>
                    ) : null}
                    <KeywordGroupEditor
                        keywords={group.Keywords}
                        maxItems={maxItems}
                        onKeywordListChange={handleKeywordListChange}
                        tracker={tracker as NewGroupTracker}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        setRef={(ref) => {
                            listContainerRef.current = ref;
                        }}
                        onError={handleListError}
                        footer={
                            // group.Keywords can still be empty right after a seeded new group is
                            // opened, while getGroupName() resolves in initializeModalSession — don't
                            // mount KeywordsSuggestions until there's an actual seed to fetch for,
                            // otherwise it fires with an empty keyword list.
                            shouldShowSuggestions && group.Keywords.length > 0 ? (
                                <KeywordsSuggestions
                                    seedKeywords={group.Keywords}
                                    isNewGroup={isNewGroup(group)}
                                    onAddKeyword={onAddSuggested}
                                    onAddMultipleKeywords={onAddMultipleSuggested}
                                    onFinishedLoading={() => setIsInitialLoad(false)}
                                    numberOfElementsToAutoPopulate={shouldAutoAddSuggestions ? 6 : 0}
                                />
                            ) : (
                                !!group.Keywords?.length && (
                                    // if we are creating a new group and have a single keyword selected, display modal for suggestions
                                    <LoadSuggestionsBanner>
                                        <SuggestionsBannerText>
                                            KeywordAnalysis.keywordgroup.wizard.suggestions.cta.text
                                        </SuggestionsBannerText>
                                        <IconButton
                                            iconName="wand"
                                            type="flat"
                                            onClick={handleLoadSuggestions}
                                        >
                                            {i18n("KeywordAnalysis.keywordgroup.wizard.suggestions.cta.button")}
                                        </IconButton>
                                    </LoadSuggestionsBanner>
                                )
                            )
                        }
                    />
                </KeywordEditorContainer>

                <footer className="create-group-success-footer">
                    <ListFooter
                        onSave={handleSave}
                        onDelete={handleDelete}
                        saveButtonEnabled={Boolean(group?.Keywords?.length && !!group?.Name?.trim().length)}
                        saveButtonTooltipText={saveButtonTooltip()}
                        isSaving={isSaving}
                        isDeleting={false}
                        showDeleteButton={showDeleteButton || !isNewGroup(group)}
                        showCancelButton
                        onCancel={() => {
                            handleClose();
                            allTrackers.dispatchTrackEvent("Pop Up", "click", "Create New Keyword Group/Cancel");
                        }}
                    />
                </footer>
            </div>
        </BasicModal>
    );
};
